import type { GridApiPro } from '@mui/x-data-grid-pro';
import { isNullOrUndefined } from '@vy/utils';
import { MutableRefObject, useCallback, useEffect, useMemo, useRef } from 'react';

type PersistMode = 'session' | 'local';

type ScrollPersistOptions = {
  mode?: PersistMode;
  debounceMs?: number;
  restoreWhen?: 'onRows' | 'immediate';
};

type ScrollPos = { top: number; left: number };

const MUI_VIRTUAL_SCROLLER_CLASS = 'MuiDataGrid-virtualScroller';

// No-op storage for environments where Web Storage is unavailable (SSR, Safari private mode, etc.)
const noopStorage: Storage = {
  getItem: () => null,
  setItem: () => {
    // No-op for restricted environments
  },
  removeItem: () => {
    // No-op for restricted environments
  },
  clear: () => {
    // No-op for restricted environments
  },
  key: () => null,
  length: 0,
};

export function usePersistedDataGridScroll(
  apiRef: MutableRefObject<GridApiPro>,
  gridId: string,
  rowsCount: number,
  options: ScrollPersistOptions = {},
) {
  const { mode = 'session', debounceMs = 200, restoreWhen = 'onRows' } = options;
  const key = useMemo(() => `mui-datagrid:scroll:${gridId}`, [gridId]);

  const restoredRef = useRef(false);
  const timerRef = useRef<number | null>(null);
  const restoreTimerRef = useRef<number | null>(null);

  const clearRestoreTimer = useCallback(() => {
    if (restoreTimerRef.current) {
      window.clearTimeout(restoreTimerRef.current);
      restoreTimerRef.current = null;
    }
  }, []);

  const restore = useCallback(() => {
    if (restoredRef.current) {
      return;
    }

    const gridApiRef = apiRef.current;

    if (isNullOrUndefined(gridApiRef)) {
      return;
    }

    let saved: ScrollPos | null = null;

    try {
      const storage = getStorage(mode);

      saved = safeParse<ScrollPos>(storage.getItem(key));
    } catch {
      // Storage access failed - mark as restored to avoid retrying
      restoredRef.current = true;
      return;
    }

    if (isNullOrUndefined(saved)) {
      restoredRef.current = true;
      return;
    }

    requestAnimationFrame(() => {
      clearRestoreTimer();

      restoreTimerRef.current = window.setTimeout(() => {
        try {
          const gridRoot = gridApiRef.rootElementRef?.current;

          if (gridRoot) {
            const virtualScroller = gridRoot.querySelector(`.${MUI_VIRTUAL_SCROLLER_CLASS}`) as HTMLElement;

            if (virtualScroller) {
              virtualScroller.scrollTop = saved.top;
              virtualScroller.scrollLeft = saved.left;
            }
          }
        } finally {
          restoredRef.current = true;
          restoreTimerRef.current = null;
        }
      }, 150);
    });
  }, [apiRef, key, mode, clearRestoreTimer]);

  const saveNow = useCallback(() => {
    const gridApiRef = apiRef.current;

    if (isNullOrUndefined(gridApiRef)) return;

    const storage = getStorage(mode);

    try {
      const currentScrollPosition = gridApiRef.getScrollPosition();

      storage.setItem(key, JSON.stringify(currentScrollPosition));
    } catch {
      // ignore (storage full / disabled)
    }
  }, [apiRef, key, mode]);

  const scheduleSave = useCallback(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
    }

    timerRef.current = window.setTimeout(saveNow, debounceMs);
  }, [debounceMs, saveNow]);

  useEffect(() => {
    if (restoreWhen === 'immediate') restore();

    return clearRestoreTimer;
  }, [restoreWhen, restore, clearRestoreTimer]);

  useEffect(() => {
    if (restoreWhen !== 'onRows') {
      return;
    }

    if (isNullOrUndefined(rowsCount)) {
      return;
    }

    restore();

    return clearRestoreTimer;
  }, [rowsCount, restoreWhen, restore, clearRestoreTimer]);

  useEffect(() => {
    const api = apiRef.current;

    if (isNullOrUndefined(api)) {
      return;
    }

    const unsubscribe = api.subscribeEvent('scrollPositionChange', scheduleSave);

    return () => {
      unsubscribe();

      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
      }

      saveNow();
    };
  }, [apiRef, saveNow, scheduleSave]);

  return {
    restore,
    saveNow,
    clear: useCallback(() => {
      try {
        const storage = getStorage(mode);
        storage.removeItem(key);
      } catch {
        // Swallow errors from restricted or unavailable storage environments
      }
    }, [key, mode]),
  };
}

function getStorage(mode: PersistMode): Storage {
  if (typeof window === 'undefined') {
    return noopStorage;
  }

  try {
    const storage = mode === 'local' ? window.localStorage : window.sessionStorage;

    storage.getItem('__test__');

    return storage;
  } catch {
    return noopStorage;
  }
}

function safeParse<T>(value: string | null): T | null {
  if (isNullOrUndefined(value)) {
    return null;
  }

  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}
